import React, { useEffect, useRef, useState } from "react";
import api from "../../api/axiosConfig"; // 👈 usamos la instancia configurada
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Spinner } from "react-bootstrap";
import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { useNavigate } from "react-router-dom";
import { FaUserAlt, FaCheck } from "react-icons/fa";

const COLUMNS = [
  { key: "PEDIDO_TOMADO", title: "📝 Pedido tomado" },
  { key: "EN_COCINA", title: "🍳 En cocina" },
  { key: "EN_JUGOS", title: "🥤 En jugos" },
  { key: "EN_MESA", title: "✅ Listo" }, // columna final
];

// 🔎 Leer rol desde localStorage (como usas en otras partes)
const getRol = () => {
  try {
    const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
    const rolPlano = localStorage.getItem("rol");
    const r = usuario?.rol || rolPlano || "MESERO";
    return String(r).toUpperCase();
  } catch {
    return "MESERO";
  }
};

function PedidoBoard() {
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const stompClientRef = useRef(null);
  const navigate = useNavigate();

  // ── Roles ─────────────────────────────
  const rol = getRol();
  const esAdminOCajero = rol === "ADMIN" || rol === "CAJERO";
  const esMesero = rol === "MESERO";
  const esCocina = rol === "COCINA";
  const esJugos = rol === "JUGOS";

  // columnas visibles según rol
  let visibleColumns = COLUMNS;
  if (esCocina) {
    visibleColumns = COLUMNS.filter((c) => c.key === "EN_COCINA");
  } else if (esJugos) {
    visibleColumns = COLUMNS.filter((c) => c.key === "EN_JUGOS");
  }

  const isSingleColumn = visibleColumns.length === 1;
  // modo grilla lateral solo para cocina/jugos
  const isGridMode = isSingleColumn && (esCocina || esJugos);

  // solo ADMIN y CAJERO pueden arrastrar
  const allowDrag = esAdminOCajero;

  // ─────────────────────────────
  // Cargar pedidos iniciales
  // ─────────────────────────────
  useEffect(() => {
    const controller = new AbortController();
    cargarPedidos(controller.signal);
    conectarWebSocket();

    return () => {
      controller.abort();
      if (stompClientRef.current && stompClientRef.current.active) {
        stompClientRef.current.deactivate();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarPedidos = async (signal) => {
    setLoading(true);
    try {
      const res = await api.get("/pedidos/abiertos", { signal });
      setPedidos(res.data || []);
    } catch (error) {
      if (error.name === "CanceledError") return;
      console.error("Error al cargar pedidos:", error);
      toast.error("⚠️ Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────
  // WebSocket STOMP + SockJS
  // ─────────────────────────────
  const conectarWebSocket = () => {
    // Base de la API: ej. "http://192.168.0.10:8080/api"
    const apiBase = api.defaults.baseURL || "";
    // Quitamos "/api" del final para obtener el host del backend
    const backendBase = apiBase.replace(/\/api\/?$/, "");

    const socketUrl =
      process.env.REACT_APP_WS_URL || `${backendBase}/ws-delis`;

    console.log("🔌 Conectando WebSocket a:", socketUrl);

    const socket = new SockJS(socketUrl);

    const client = new Client({
      webSocketFactory: () => socket,
      reconnectDelay: 5000,
      onConnect: () => {
        console.log("✅ WebSocket conectado");

        client.subscribe("/topic/pedidos", (message) => {
          try {
            const pedido = JSON.parse(message.body);
            upsertPedido(pedido);
          } catch (e) {
            console.error("Error parseando mensaje de WS", e);
          }
        });
      },
      onStompError: (frame) => {
        console.error("Error STOMP:", frame.headers["message"]);
      },
    });

    client.activate();
    stompClientRef.current = client;
  };

  const upsertPedido = (pedidoNuevo) => {
    setPedidos((prev) => {
      const existe = prev.some((p) => p.id === pedidoNuevo.id);

      // Si quisieras sacar del tablero pedidos cerrados, podrías hacer algo tipo:
      // if (pedidoNuevo.entregado && pedidoNuevo.facturado) {
      //   return prev.filter((p) => p.id !== pedidoNuevo.id);
      // }

      if (!existe) return [...prev, pedidoNuevo];
      return prev.map((p) => (p.id === pedidoNuevo.id ? pedidoNuevo : p));
    });
  };

  // ─────────────────────────────
  // Cambiar estado (drag & drop + botón)
  // ─────────────────────────────
  const actualizarEstado = async (pedidoId, nuevoEstado) => {
    try {
      const res = await api.patch(`/pedidos/${pedidoId}/estado`, {
        estado: nuevoEstado,
      });

      // actualización optimista local
      setPedidos((prev) =>
        prev.map((p) =>
          p.id === pedidoId ? { ...p, estado: nuevoEstado } : p
        )
      );

      console.log("Respuesta cambiar estado:", res.data);
      toast.success("✅ Estado actualizado");
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      const msgBackend =
        error.response?.data?.message ||
        error.response?.data ||
        "No se pudo actualizar el estado";
      toast.error(`⚠️ ${msgBackend}`);
    }
  };

  const handleDragStart = (pedidoId) => setDraggingId(pedidoId);
  const handleDragEnd = () => setDraggingId(null);

  const handleDropOnColumn = (estadoColumna) => {
    if (!allowDrag) return;
    if (!draggingId) return;
    const pedido = pedidos.find((p) => p.id === draggingId);
    if (!pedido) return;
    if (pedido.estado === estadoColumna) return;
    actualizarEstado(draggingId, estadoColumna);
    setDraggingId(null);
  };

  const handleDragOver = (e) => {
    if (!allowDrag) return;
    e.preventDefault();
  };

  const pedidosPorEstado = (estado) =>
    pedidos.filter((p) => p.estado === estado && !p.cancelado );

  const calcularTotalPedido = (pedido) =>
    (pedido?.detalles || []).reduce(
      (acc, d) => acc + (parseFloat(d.subtotal) || 0),
      0
    );

  // ─────────────────────────────
  // Lógica de “Listo”
  // ─────────────────────────────

  // 1) Desde PEDIDO_TOMADO → decidir si va a COCINA o JUGOS
  const decidirEstadoInicial = (pedido) => {
    const detalles = pedido.detalles || [];

    const tieneRapidasODesayuno = detalles.some((d) => {
      const valor = String(d.producto?.categoria || "").toLowerCase().trim();
      return (
        valor.includes("rapida") ||
        valor.includes("rápida") ||
        valor.includes("comida") ||
        valor.includes("desayuno")
      );
    });

    return tieneRapidasODesayuno ? "EN_COCINA" : "EN_JUGOS";
  };

  // Quién puede ver el botón Listo
  const puedeMostrarBotonListo = (pedido) => {
    if (esMesero) return false; // mesero solo mira

    if (esAdminOCajero) {
      return (
        pedido.estado === "PEDIDO_TOMADO" ||
        pedido.estado === "EN_COCINA" ||
        pedido.estado === "EN_JUGOS"
      );
    }

    if (esCocina) return pedido.estado === "EN_COCINA";
    if (esJugos) return pedido.estado === "EN_JUGOS";

    return false;
  };

  // 2) Botón “Listo” según el estado actual y rol
  const manejarClickListo = (pedido) => {
    let siguiente = null;

    if (esAdminOCajero) {
      switch (pedido.estado) {
        case "PEDIDO_TOMADO":
          siguiente = decidirEstadoInicial(pedido);
          break;
        case "EN_COCINA":
          siguiente = "EN_JUGOS";
          break;
        case "EN_JUGOS":
          siguiente = "EN_MESA";
          break;
        default:
          return;
      }
    } else if (esCocina && pedido.estado === "EN_COCINA") {
      // cocina solo puede mover de cocina → jugos
      siguiente = "EN_JUGOS";
    } else if (esJugos && pedido.estado === "EN_JUGOS") {
      // jugos solo puede mover de jugos → listo
      siguiente = "EN_MESA";
    } else {
      return;
    }

    actualizarEstado(pedido.id, siguiente);
  };

  // ─────────────────────────────
  // Render
  // ─────────────────────────────
  return (
    <>
      <ToastContainer position="top-right" autoClose={2500} />

      <div className="bg-white/95 text-slate-900 dark:bg-slate-900/90 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 mt-3">
        {/* Encabezado */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-amber-500 dark:text-amber-300">
              Tablero de Pedidos
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Cocina y jugos trabajan en tiempo real.
              {esMesero && " Solo visualización para meseros."}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => cargarPedidos()}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-400 dark:border-slate-600 text-xs sm:text-sm text-slate-700 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" /> Actualizando...
                </>
              ) : (
                "Actualizar"
              )}
            </button>

            <button
              onClick={() => navigate("/pedidos")}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-100 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            >
              Volver a lista
            </button>
          </div>
        </div>

        {/* Tablero tipo Kanban */}
        <div className="mt-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/60 p-3">
          {loading && pedidos.length === 0 ? (
            <div className="flex justify-center items-center py-6 text-sm text-slate-500 dark:text-slate-400">
              <Spinner animation="border" size="sm" className="mr-2" />
              Cargando pedidos...
            </div>
          ) : (
            <div
              className={
                isSingleColumn
                  ? "flex justify-center"
                  : "flex gap-3 overflow-x-auto pb-2"
              }
            >
              {visibleColumns.map((col) => (
                <div
                  key={col.key}
                  className={
                    isSingleColumn
                      ? "flex flex-col bg-white/95 dark:bg-slate-900/90 rounded-xl border border-slate-200 dark:border-slate-700 w-full max-w-5xl"
                      : "flex flex-col bg-white/95 dark:bg-slate-900/90 rounded-xl border border-slate-200 dark:border-slate-700 min-w-[230px] w-64 max-w-xs"
                  }
                  onDragOver={handleDragOver}
                  onDrop={() => handleDropOnColumn(col.key)}
                >
                  {/* Header de columna */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-100">
                      {col.title}
                    </span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/40">
                      {pedidosPorEstado(col.key).length}
                    </span>
                  </div>

                  {/* Cards */}
                  <div
                    className={
                      isGridMode
                        ? "px-2.5 py-2 flex flex-wrap gap-3"
                        : "flex-1 px-2.5 py-2 space-y-2"
                    }
                  >
                    {pedidosPorEstado(col.key).length === 0 ? (
                      <div className="text-[11px] text-center text-slate-400 dark:text-slate-500 py-4 w-full">
                        Sin pedidos aquí
                      </div>
                    ) : (
                      pedidosPorEstado(col.key).map((pedido) => (
                        <div
                          key={pedido.id}
                          className={`rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2.5 py-2 text-xs ${
                            allowDrag ? "cursor-grab" : "cursor-default"
                          } hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors ${
                            draggingId === pedido.id ? "opacity-50" : ""
                          } ${isGridMode ? "w-64" : ""}`}
                          draggable={allowDrag}
                          onDragStart={
                            allowDrag ? () => handleDragStart(pedido.id) : undefined
                          }
                          onDragEnd={allowDrag ? handleDragEnd : undefined}
                          onDoubleClick={() =>
                            navigate(`/pedidos/${pedido.id}`)
                          }
                        >
                          {/* Header card: ID + Mesa */}
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="font-semibold text-slate-800 dark:text-slate-100">
                              Pedido #{pedido.id}
                            </span>
                            {pedido.mesa && (
                              <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-800 text-amber-300">
                                Mesa {pedido.mesa.numero || "—"}
                              </span>
                            )}
                          </div>

                          {/* Quién tomó el pedido */}
                          <div className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-300 mb-1">
                            <FaUserAlt className="text-slate-400" size={10} />
                            <span>
                              Tomado por:{" "}
                              <span className="font-semibold">
                                {pedido.mesero?.nombre || "—"}
                              </span>
                            </span>
                          </div>

                          {/* Detalles: nombre del producto + descripcion del detalle */}
                          <div className="text-[11px] text-slate-600 dark:text-slate-300 mb-1">
                            {pedido.detalles && pedido.detalles.length > 0 ? (
                              <ul className="space-y-0.5">
                                {pedido.detalles.slice(0, 3).map((det) => (
                                  <li key={det.id}>
                                    {det.cantidad}x{" "}
                                    {det.producto?.nombre || "Producto"}
                                    {det.descripcion && (
                                      <span className="text-[10px] text-slate-400">
                                        {" — "}
                                        {det.descripcion}
                                      </span>
                                    )}
                                  </li>
                                ))}
                                {pedido.detalles.length > 3 && (
                                  <li className="italic text-slate-400 dark:text-slate-500">
                                    + {pedido.detalles.length - 3} ítems más...
                                  </li>
                                )}
                              </ul>
                            ) : (
                              <span className="italic text-slate-400 dark:text-slate-500">
                                Sin detalles
                              </span>
                            )}
                          </div>

                          {/* Nota general del pedido SOLO en columna Listo (EN_MESA) */}
                          {col.key === "EN_MESA" && pedido.descripcion && (
                            <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 italic">
                              Nota: {pedido.descripcion}
                            </div>
                          )}

                          {/* Footer card */}
                          <div className="flex items-center justify-between mt-1">
                            <span className="font-semibold text-[11px] text-emerald-500">
                              $
                              {calcularTotalPedido(pedido).toLocaleString(
                                "es-CO",
                                {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                }
                              )}
                            </span>
                            {pedido.fecha && (
                              <span className="text-[11px] text-slate-400">
                                {new Date(pedido.fecha).toLocaleTimeString(
                                  "es-CO",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </span>
                            )}
                          </div>

                          {/* Botón "Listo" según rol */}
                          {puedeMostrarBotonListo(pedido) && (
                            <button
                              onClick={() => manejarClickListo(pedido)}
                              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-semibold bg-amber-500 text-slate-900 hover:bg-amber-400 transition-colors"
                            >
                              <FaCheck size={10} />
                              Listo
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default PedidoBoard;